import { useState, useEffect } from "react";
import { Box, GlobalStyles, Typography } from "@mui/material";

const LOGO = "/loaderLogo.png";

const NAVY_DARK = "#0B2942";
const BLUE = "#4C9DE8";

const keyframes = `
@keyframes splash-logo-in {
  0% {
    opacity: 0;
    transform: scale(0.86);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes splash-glow {
  0%, 100% {
    opacity: 0.35;
    transform: scale(0.92);
  }
  50% {
    opacity: 0.65;
    transform: scale(1.08);
  }
}

@keyframes splash-ring {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes splash-ring-reverse {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
}

@keyframes splash-text-in {
  0% {
    opacity: 0;
    transform: translateY(12px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes splash-line {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}

@keyframes splash-dot {
  0%, 100% {
    opacity: 0.25;
    transform: scale(0.75);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes splash-light {
  0% {
    transform: translate(-10%, -10%);
    opacity: 0;
  }
  25% {
    opacity: 0.5;
  }
  75% {
    opacity: 0.25;
  }
  100% {
    transform: translate(15%, 10%);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .splash-animated {
    animation-duration: 8s !important;
  }
}
`;

const FlashScreen = ({ duration = 5000, onComplete }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);

      if (onComplete) {
        onComplete();
      }
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onComplete]);

  if (!visible) return null;

  return (
    <>
      <GlobalStyles styles={keyframes} />

      <Box
        sx={{
          position: "fixed",
          inset: 0,

          width: "100vw",
          height: "100vh",

          overflow: "hidden",

          zIndex: 1500,

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          backgroundColor: NAVY_DARK,
        }}
      >
        {/* =================================================
            BACKGROUND LIGHT
        ================================================= */}

        <Box
          className="splash-animated"
          sx={{
            position: "absolute",

            width: {
              xs: 420,
              sm: 620,
              md: 760,
            },

            height: {
              xs: 420,
              sm: 620,
              md: 760,
            },

            borderRadius: "50%",

            background:
              "radial-gradient(circle, rgba(76,157,232,0.16) 0%, rgba(76,157,232,0.07) 35%, transparent 70%)",

            filter: "blur(8px)",

            animation: "splash-glow 4s ease-in-out infinite",

            pointerEvents: "none",
          }}
        />

        {/* Moving soft light */}

        <Box
          className="splash-animated"
          sx={{
            position: "absolute",

            width: 380,
            height: 380,

            borderRadius: "50%",

            background:
              "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 68%)",

            filter: "blur(18px)",

            animation: "splash-light 7s ease-in-out infinite",

            pointerEvents: "none",
          }}
        />

        {/* =================================================
            SUBTLE GRID / DOT PATTERN
        ================================================= */}

        <Box
          sx={{
            position: "absolute",
            inset: 0,

            opacity: 0.08,

            backgroundImage:
              "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)",

            backgroundSize: "28px 28px",

            maskImage:
              "radial-gradient(circle at center, black 0%, transparent 72%)",

            WebkitMaskImage:
              "radial-gradient(circle at center, black 0%, transparent 72%)",

            pointerEvents: "none",
          }}
        />

        {/* =================================================
            TOP BRAND LABEL
        ================================================= */}

        <Box
          sx={{
            position: "absolute",

            top: {
              xs: 24,
              sm: 32,
            },

            left: {
              xs: 24,
              sm: 40,
            },

            display: "flex",
            alignItems: "center",

            gap: 1,

            color: "rgba(255,255,255,0.55)",
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,

              borderRadius: "50%",

              backgroundColor: BLUE,

              boxShadow: "0 0 8px rgba(76,157,232,0.7)",
            }}
          />

          <Typography
            sx={{
              fontSize: 11,

              fontWeight: 600,

              letterSpacing: "1.8px",

              textTransform: "uppercase",
            }}
          >
            Parivikshak
          </Typography>
        </Box>

        {/* =================================================
            MAIN BRAND CONTENT
        ================================================= */}

        <Box
          sx={{
            position: "relative",

            zIndex: 2,

            display: "flex",

            flexDirection: "column",

            alignItems: "center",

            textAlign: "center",

            transform: "translateY(-10px)",
          }}
        >
          {/* =================================================
              LOGO CONTAINER
          ================================================= */}

          <Box
            sx={{
              position: "relative",

              width: {
                xs: 170,
                sm: 190,
              },

              height: {
                xs: 170,
                sm: 190,
              },

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              animation: "splash-logo-in 650ms ease-out",
            }}
          >
            {/* Glow behind logo */}

            <Box
              className="splash-animated"
              sx={{
                position: "absolute",

                width: {
                  xs: 150,
                  sm: 168,
                },

                height: {
                  xs: 150,
                  sm: 168,
                },

                borderRadius: "50%",

                background:
                  "radial-gradient(circle, rgba(76,157,232,0.20) 0%, rgba(76,157,232,0.06) 55%, transparent 75%)",

                filter: "blur(2px)",

                animation: "splash-glow 2.8s ease-in-out infinite",
              }}
            />

            {/* Outer ring: radial ticks */}
            <Box
              className="splash-animated"
              component="svg"
              viewBox="0 0 190 190"
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                animation: "splash-ring-reverse 18s linear infinite",
                WebkitMaskImage:
                  "conic-gradient(from 180deg, transparent 0deg, #000 100deg, #000 180deg, transparent 300deg)",
                maskImage:
                  "conic-gradient(from 180deg, transparent 0deg, #000 100deg, #000 180deg, transparent 300deg)",
              }}
            >
              {/* long ticks */}
              <circle
                cx="95"
                cy="95"
                r="88"
                fill="none"
                stroke="#9FD0FF"
                strokeWidth="12"
                strokeDasharray="1.6 7.615"
              />
              {/* short ticks, offset between the long ones */}
              <circle
                cx="95"
                cy="95"
                r="88"
                fill="none"
                stroke="rgba(159,208,255,0.55)"
                strokeWidth="6"
                strokeDasharray="1.2 8.015"
                strokeDashoffset="-4.6"
              />
            </Box>

            {/* Inner ring: thick gradient arc */}
            <Box
              className="splash-animated"
              component="svg"
              viewBox="0 0 160 160"
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: { xs: 154, sm: 160 },
                height: { xs: 154, sm: 160 },
                animation: "splash-ring 5s linear infinite",
                filter: "drop-shadow(0 0 6px rgba(109,181,244,0.45))",
              }}
            >
              <defs>
                <linearGradient
                  id="splash-arc-grad"
                  x1="0"
                  y1="1"
                  x2="0"
                  y2="0"
                >
                  <stop offset="0%" stopColor="#FFFFFF" />
                  <stop offset="55%" stopColor="#CFE6FB" />
                  <stop offset="100%" stopColor="#4C9DE8" stopOpacity="0.55" />
                </linearGradient>
              </defs>

              <circle
                cx="80"
                cy="80"
                r="72"
                fill="none"
                stroke="rgba(255,255,255,0.10)"
                strokeWidth="1.5"
              />
              <circle
                cx="80"
                cy="80"
                r="72"
                fill="none"
                stroke="url(#splash-arc-grad)"
                strokeWidth="7"
                strokeLinecap="round"
                strokeDasharray="130 322.4"
                transform="rotate(100 80 80)"
              />
            </Box>

            {/* =================================================
                LOGO MEDALLION
            ================================================= */}

            <Box
              sx={{
                position: "relative",

                width: {
                  xs: 116,
                  sm: 126,
                },

                height: {
                  xs: 116,
                  sm: 126,
                },

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                borderRadius: "50%",

                background:
                  "linear-gradient(145deg, #FFFFFF 0%, #F3F8FC 55%, #DDECF8 100%)",

                border: "1px solid rgba(255,255,255,0.85)",

                boxShadow:
                  "0 20px 40px rgba(0,0,0,0.22), inset 0 2px 5px rgba(255,255,255,0.95), inset 0 -4px 9px rgba(90,120,150,0.18)",
              }}
            >
              <Box
                component="img"
                src={LOGO}
                alt="Parivikshak"
                sx={{
                  width: {
                    xs: 68,
                    sm: 74,
                  },

                  height: {
                    xs: 68,
                    sm: 74,
                  },

                  objectFit: "contain",

                  display: "block",
                }}
              />
            </Box>

            {/* Small status dot */}
          </Box>

          {/* =================================================
              BRAND NAME
          ================================================= */}

          <Typography
            sx={{
              mt: 1.5,

              fontSize: {
                xs: 25,
                sm: 30,
              },

              fontWeight: 700,

              letterSpacing: {
                xs: "4px",
                sm: "6px",
              },

              color: "#FFFFFF",

              lineHeight: 1.15,

              animation: "splash-text-in 700ms ease-out",
            }}
          >
            PARIVIKSHAK
          </Typography>

          {/* =================================================
              PRODUCT DESCRIPTION
          ================================================= */}

          <Typography
            sx={{
              mt: 1,

              fontSize: {
                xs: 10,
                sm: 11,
              },

              fontWeight: 500,

              letterSpacing: "2.2px",

              textTransform: "uppercase",

              color: "rgba(255,255,255,0.58)",

              animation: "splash-text-in 900ms ease-out",
            }}
          >
            Safety Management System
          </Typography>

          {/* =================================================
              PROGRESS
          ================================================= */}

          <Box
            sx={{
              mt: 3.5,

              width: {
                xs: 190,
                sm: 230,
              },
            }}
          >
            {/* Track */}

            <Box
              sx={{
                width: "100%",

                height: 2,

                borderRadius: 10,

                overflow: "hidden",

                backgroundColor: "rgba(255,255,255,0.16)",
              }}
            >
              {/* Progress */}

              <Box
                sx={{
                  height: "100%",

                  background: "linear-gradient(90deg, #2F80C9, #6DB5F4)",

                  borderRadius: 10,

                  animation: `splash-line ${duration}ms linear forwards`,
                }}
              />
            </Box>

            {/* Status */}

            <Box
              sx={{
                mt: 1,

                display: "flex",

                alignItems: "center",

                justifyContent: "center",

                gap: 0.8,
              }}
            >
              <Box
                className="splash-animated"
                sx={{
                  width: 4,
                  height: 4,

                  borderRadius: "50%",

                  backgroundColor: BLUE,

                  animation: "splash-dot 1.2s ease-in-out infinite",
                }}
              />

              <Typography
                sx={{
                  fontSize: 10,

                  fontWeight: 500,

                  letterSpacing: "0.8px",

                  color: "rgba(255,255,255,0.48)",
                }}
              >
                Initializing application
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* =================================================
            BOTTOM
        ================================================= */}

        <Typography
          sx={{
            position: "absolute",

            bottom: {
              xs: 20,
              sm: 28,
            },

            left: 0,
            right: 0,

            textAlign: "center",

            fontSize: 10,

            color: "rgba(255,255,255,0.30)",

            letterSpacing: "0.6px",
          }}
        >
          Secure • Reliable • Safety First
        </Typography>
      </Box>
    </>
  );
};

export default FlashScreen;
